import type { PhoneFieldInterface } from '@sunwebgroup/ui-components/uds'
import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import ServiceBox from '@/scopes/checkout/components/services/ServiceBox'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import {
  Banner,
  ButtonLoaderIcon,
  countryData,
  Icon,
  mqMin,
  space,
  SpaceBetween,
  tokPx,
} from '@sunwebgroup/ui-components'
import { Button, PhoneField, TextField, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { styled } from 'styled-components'
import { createErrorEvent } from '../../domain/analytics'
import { useBookingDraftQuery, useEngineErrorContactInfo } from '../../domain/hooks'
import { emailPattern, namePattern, useSmartFieldStatus } from '../helpers/Validations'
import { useEngineErrorContactInfoStore } from './store'

interface FormValues {
  name: string
  email: string
  phone: PhoneFieldInterface
}

const FormWrapper = styled.form`
  gap: ${space('primitives_space_16')};
  display: flex;
  flex-direction: column;
`

const SubmitButton = styled(Button)`
  width: ${tokPx('primitives_area_size_240')};
  height: ${tokPx('primitives_space_48')};
  ${ButtonLoaderIcon} {
    ${Icon.modSize('lg')}
  }
`

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${space('primitives_space_16')};
  margin-bottom: ${space('primitives_space_16')};

  > * {
    height: 100%;
    align-items: center;
    width: ${tokPx('primitives_area_size_720')};
  }

  @media ${mqMin('primitives_breakpoint_m_min')} {
    margin-top: ${space('primitives_space_24')};
    margin-bottom: ${space('primitives_space_24')};
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    margin-top: ${space('primitives_space_32')};
    margin-bottom: ${space('primitives_space_32')};
  }
`

export function EngineError() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const hasBeenSent = React.useRef(false)
  const [isChecking, setIsChecking] = React.useState(false)
  const [isSent, setIsSet] = React.useState(false)
  const [selectedCountryMobile, setSelectedCountryMobile] = React.useState(countryData[0])

  const engineErrorConfig = useCMSPageField('Checkout.engineError')
  const { data } = useBookingDraftQuery()

  const { setValue, formState, control, handleSubmit, getFieldState, watch } = useForm<FormValues>({ mode: 'all' })
  const errorsTyped = formState.errors as Record<keyof FormValues, { message: string }>

  const { sendEngineErrorContactInfo } = useEngineErrorContactInfo()

  const engineErrorStore = useEngineErrorContactInfoStore(state => state.result)
  const setBookingInfoError = useEngineErrorContactInfoStore(state => state.setEngineErrorContactInfoResponse)

  React.useEffect(() => {
    if (engineErrorStore === true) {
      setIsChecking(false)
      setIsSet(true)
    }
    else if (engineErrorStore === false) {
      setIsChecking(false)
    }
  }, [engineErrorStore])

  const onSubmit = (result: FormValues) => {
    setIsChecking(true)
    sendEngineErrorContactInfo({
      customerName: result.name,
      customerEmail: result.email,
      customerPhone: result.phone ? `+${result.phone.countryCode + result.phone.phoneNumber}` : '',
    })
  }

  const filteredCountry = engineErrorConfig?.PhoneInputSettings
    ? engineErrorConfig.PhoneInputSettings.map(setting =>
        countryData.find(country => country.isoCode === setting.IsoCode),
      ).filter((country): country is NonNullable<typeof country> => Boolean(country))
    : countryData

  const nameFieldState = getFieldState(`name`, formState)
  const watchedNameFieldValue = watch(`name`) || ''
  const statusNameFieldValue = useSmartFieldStatus({
    value: watchedNameFieldValue,
    error: errorsTyped.name,
    isDirty: nameFieldState?.isDirty ?? false,
    fieldLengthValidation: 0,
    isSubmitting: formState?.isSubmitting ?? false,
    matchPattern: new RegExp(namePattern).test(watchedNameFieldValue),
  })

  const phoneFieldState = getFieldState(`phone`, formState)
  const watchedPhoneNumberFieldValue = watch(`phone.phoneNumber`) || ''
  const statusPhoneFieldName = useSmartFieldStatus({
    value: watchedPhoneNumberFieldValue,
    error: errorsTyped.phone,
    isDirty: phoneFieldState?.isDirty ?? false,
    fieldLengthValidation: 0,
    isSubmitting: formState?.isSubmitting ?? false,
    matchPattern: new RegExp(selectedCountryMobile.pattern).test(watchedPhoneNumberFieldValue),
  })

  const eMailFieldState = getFieldState(`email`, formState)
  const watchedeMailFieldValue = watch(`email`) || ''
  const statuseMailFieldName = useSmartFieldStatus({
    value: watchedeMailFieldValue,
    error: errorsTyped.email,
    isDirty: eMailFieldState?.isDirty ?? false,
    fieldLengthValidation: 0,
    isSubmitting: formState?.isSubmitting ?? false,
    matchPattern: new RegExp(emailPattern).test(watchedeMailFieldValue),
  })

  React.useEffect(() => {
    if (hasBeenSent.current)
      return
    sendEvent(createErrorEvent({ description: data?.error?.message ?? '' }))
    hasBeenSent.current = true
  }, [sendEvent, hasBeenSent, data?.error?.message])

  return (
    <Wrapper>
      <ServiceBox title="">
        <SpaceBetween spacing={space('primitives_space_20')} direction="y">
          {engineErrorStore === undefined && (
            <>
              <Banner $type="error">
                {engineErrorConfig && data?.error && (
                  <Typography
                    dangerouslySetInnerHTML={{
                      __html: replaceErrorMessage(engineErrorConfig.TextError, data.error.message),
                    }}
                  />
                )}
              </Banner>
              {engineErrorConfig && (
                <Typography dangerouslySetInnerHTML={{ __html: engineErrorConfig.TextDescription }} />
              )}
              <FormWrapper onSubmit={handleSubmit(onSubmit)}>
                <Controller
                  name="name"
                  control={control}
                  rules={{
                    required: true,
                    pattern: {
                      value: namePattern,
                      message: intl.formatMessage({ id: 'checkout_steps_engine_error_name_invalid' }),
                    },
                  }}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={intl.formatMessage({ id: 'checkout_steps_engine_error_name' })}
                      status={statusNameFieldValue}
                      feedbackText={
                        errorsTyped.name?.message
                        || intl.formatMessage({ id: 'checkout_steps_engine_error_name_invalid' })
                      }
                      onChange={field.onChange}
                      onBlur={(e) => {
                        field.onBlur()
                        const newValue = e.target.value
                        setValue('name', newValue, {
                          shouldValidate: true,
                        })
                      }}
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: true,
                    pattern: {
                      value: emailPattern,
                      message: intl.formatMessage({ id: 'checkout_steps_engine_error_mail_invalid' }),
                    },
                  }}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      type="email"
                      status={statuseMailFieldName}
                      feedbackText={intl.formatMessage({ id: 'checkout_steps_engine_error_mail_invalid' })}
                      label={intl.formatMessage({ id: 'checkout_steps_engine_error_mail' })}
                      {...field}
                      onChange={field.onChange}
                      onBlur={(e) => {
                        field.onBlur()
                        const newValue = e.target.value
                        setValue('email', newValue, {
                          shouldValidate: true,
                        })
                      }}
                    />
                  )}
                />
                <div>
                  <Typography>{intl.formatMessage({ id: 'checkout_steps_engine_error_phone' })}</Typography>
                  <Controller
                    name="phone"
                    control={control}
                    rules={{
                      required: true,
                      validate: (value) => {
                        const phoneNumberWithoutPrefix = value?.phoneNumber
                        const phonePattern = new RegExp(selectedCountryMobile.pattern)
                        return phonePattern.test(phoneNumberWithoutPrefix)
                      },
                    }}
                    render={({ field }) => (
                      <PhoneField
                        {...field}
                        countries={filteredCountry}
                        id="phone"
                        value={field.value?.phoneNumber || ''}
                        countryCode={field.value?.countryCode ?? selectedCountryMobile.code}
                        status={statusPhoneFieldName}
                        onPhoneChange={field.onChange}
                        onCountryChange={setSelectedCountryMobile}
                        feedbackText={intl.formatMessage({ id: 'checkout_steps_engine_error_telephone_invalid' })}
                      />
                    )}
                  />
                </div>
                {!isSent && (
                  <SubmitButton $type="primary" type="submit" disabled={isChecking}>
                    {isChecking
                      ? (
                          <ButtonLoaderIcon />
                        )
                      : (
                          intl.formatMessage({ id: 'checkout_steps_engine_error_submit_contact_button' })
                        )}
                  </SubmitButton>
                )}
              </FormWrapper>
            </>
          )}

          {engineErrorStore === false && (
            <>
              <Banner $type="error">
                <div>
                  <Typography $variant="body-m-bold-flat" style={{ display: 'block' }}>
                    {intl.formatMessage({ id: 'checkout_steps_engine_contact_sent_nok' })}
                  </Typography>
                  <Typography style={{ display: 'block' }}>{engineErrorConfig?.ContactFormResponseNok}</Typography>
                </div>
              </Banner>
              <SubmitButton
                $type="primary"
                onClick={() => {
                  setBookingInfoError(undefined)
                }}
              >
                {intl.formatMessage({ id: 'checkout_steps_engine_contact_sent_retry' })}
              </SubmitButton>
            </>
          )}

          {engineErrorStore === true && (
            <Banner $type="success">
              <div>
                <Typography $variant="body-m-bold-flat" style={{ display: 'block' }}>
                  {intl.formatMessage({ id: 'checkout_steps_engine_contact_sent_ok' })}
                </Typography>
                <Typography style={{ display: 'block' }}>{engineErrorConfig?.ContactFormResponseOk}</Typography>
              </div>
            </Banner>
          )}
        </SpaceBetween>
      </ServiceBox>
    </Wrapper>
  )
}

function replaceErrorMessage(errorConfigDescription: string, errorMessage: string) {
  return errorConfigDescription.replace('{ERROR_MESSAGE}', errorMessage || '')
}
